import { Link } from "react-router-dom";
import { ArrowRight, Compass, Route, Heart, Sparkles, Users, MapPin } from "lucide-react";
import SectionEyebrow from "@/components/shared/SectionEyebrow";
import FinalCTA from "@/components/shared/FinalCTA";
import ProtectedImage from "@/components/shared/ProtectedImage";
import { SITE } from "@/lib/siteConfig";

const SERVICES = [
  {
    icon: Compass,
    title: "Leadership That Comes from Within",
    label: "Executive & Leadership Coaching",
    text: "Executive and leadership coaching to help senior professionals perform better decide with clarity and lead with authentic confidence.",
    to: "/professional-coaching",
    image: SITE.coachImage,
    imageAlt: "Ashutosh Pandey, Executive and leadership coach at NextChapter4You",
    testId: "service-card-coaching",
    protected: true,
  },
  {
    icon: Route,
    title: "Your Career Transition Handled with Care",
    label: "Expat Outplacement Support",
    text: "Personalised support for expat professionals navigating the Dutch job market, CV LinkedIn interviews and beyond.",
    to: "/expat-outplacement-support",
    image: "https://images.unsplash.com/photo-1521791136064-7986c2920216?fm=jpg&q=80&w=1400&auto=format&fit=crop",
    imageAlt: "A confident professional in career transition ready for the next role",
    testId: "service-card-outplacement",
  },
];

const WHY_US = [
  {
    icon: Heart,
    title: "Human-First Always",
    text: "Coaching and career support delivered with empathy at the centre.",
  },
  {
    icon: Sparkles,
    title: "Practical Not Just Advisory",
    text: "We walk alongside you session by session until real change lands.",
  },
  {
    icon: Users,
    title: "Executive-Level Perspective",
    text: "Coaching led by a senior leader who has sat in the seat not just studied it.",
  },
  {
    icon: MapPin,
    title: "Netherlands & EU Experience",
    text: "Deep local knowledge, international mindset.",
  },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative bg-[#F4F7FC] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center pt-12 md:pt-20 pb-20 md:pb-28">
          <div className="lg:col-span-7 relative z-10">
            <SectionEyebrow className="mb-6">{SITE.tagline}</SectionEyebrow>
            <h1
              data-testid="home-hero-headline"
              className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#1E2761] leading-[1.02] tracking-tight mb-6"
            >
              Your Next Chapter
              <br />
              <span className="italic text-[#1E2761]/90">Starts Here.</span>
            </h1>
            <p className="text-base md:text-lg text-[#334155]/85 leading-relaxed max-w-xl mb-10">
              We support senior leaders to perform at their best and guide
              professionals through career transitions with expertise empathy
              and genuine care.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <a
                href="#services"
                data-testid="hero-cta-explore"
                className="group inline-flex items-center gap-3 bg-[#1E2761] text-white px-7 py-3.5 font-medium tracking-wide hover:bg-[#161d4b] transition-colors"
              >
                Explore Our Services
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <Link
                to="/contact"
                data-testid="hero-cta-consultation"
                className="inline-flex items-center gap-3 border border-[#1E2761] text-[#1E2761] px-7 py-3.5 font-medium tracking-wide hover:bg-white transition-colors"
              >
                Book a Free Consultation
              </Link>
            </div>

            <div className="mt-14 flex items-center gap-6 text-xs uppercase tracking-[0.18em] text-[#334155]/60">
              <span>Eindhoven</span>
              <span className="h-[1px] w-6 bg-[#334155]/30" />
              <span>Netherlands</span>
              <span className="h-[1px] w-6 bg-[#334155]/30" />
              <span>Global</span>
              <span className="h-[1px] w-6 bg-[#334155]/30" />
              <span>Trusted Coaching Partner</span>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1497215728101-856f4ea42174?fm=jpg&q=85&w=1400&auto=format&fit=crop"
                alt="A bright high-rise office workspace with desk, laptop and city skyline through floor-to-ceiling windows"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#1E2761]/30 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-6 w-32 h-32 border-l-2 border-b-2 border-[#C9A84C] hidden md:block" />
            <div className="absolute -top-6 -right-6 w-32 h-32 border-r-2 border-t-2 border-[#1E2761]/20 hidden md:block" />
          </div>
        </div>
      </section>

      {/* 3 SERVICES */}
      <section id="services" className="bg-white py-24 md:py-32 px-6 md:px-10" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-16">
            <SectionEyebrow className="mb-4">What We Do</SectionEyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-[#1E2761] tracking-tight leading-tight">
              Two Ways We Help You Move Forward
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {SERVICES.map((s) => {
              const Icon = s.icon;
              return (
                <Link
                  key={s.to}
                  to={s.to}
                  data-testid={s.testId}
                  className="group bg-white border border-[#E2E8F0] hover:border-[#C9A84C]/50 hover:-translate-y-2 hover:shadow-2xl transition-all duration-300 flex flex-col"
                >
                  <div className="aspect-[4/3] overflow-hidden relative">
                    {s.protected ? (
                      <ProtectedImage
                        src={s.image}
                        alt={s.imageAlt}
                        imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    ) : (
                      <img
                        src={s.image}
                        alt={s.imageAlt}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    )}
                  </div>
                  <div className="p-8 flex-1 flex flex-col">
                    <div className="w-12 h-12 border border-[#C9A84C] flex items-center justify-center mb-5 text-[#C9A84C]">
                      <Icon size={20} strokeWidth={1.5} />
                    </div>
                    <div className="text-xs uppercase tracking-[0.16em] text-[#334155]/60 mb-2">
                      {s.label}
                    </div>
                    <h3 className="font-display text-xl md:text-2xl text-[#1E2761] mb-3 tracking-tight leading-snug">
                      {s.title}
                    </h3>
                    <p className="text-[#334155]/80 text-sm leading-relaxed flex-1">
                      {s.text}
                    </p>
                    <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#1E2761] group-hover:text-[#C9A84C] transition-colors">
                      Learn More
                      <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* FOUNDER TRUST */}
      <section className="bg-[#F4F7FC] py-24 md:py-32 px-6 md:px-10" data-testid="founder-trust">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-4">
            <div className="relative aspect-square overflow-hidden">
              <ProtectedImage
                src={SITE.founderImage}
                alt="Ashutosh Pandey, Performance Coach and Founder of NextChapter4You"
              />
              <div className="absolute -bottom-4 -right-4 w-20 h-20 border-r-2 border-b-2 border-[#C9A84C]" />
            </div>
          </div>
          <div className="md:col-span-8">
            <div className="border-l-4 border-[#C9A84C] pl-8 py-2">
              <p className="font-display text-2xl md:text-3xl text-[#1E2761] leading-snug tracking-tight mb-6 italic">
                "I started NextChapter4You.nl because I believe growth is
                deeply personal. Whether you are a leader finding your voice
                or a professional rebuilding your career, every chapter
                deserves the right support."
              </p>
              <div className="text-sm">
                <div className="font-semibold text-[#1E2761]">{SITE.founder}</div>
                <div className="text-[#334155]/70">{SITE.founderTitle}</div>
              </div>
              <Link
                to="/about-us"
                data-testid="founder-trust-cta"
                className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#1E2761] border-b border-[#C9A84C] pb-1 hover:gap-3 transition-all"
              >
                Read Our Story <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-white py-24 md:py-32 px-6 md:px-10" data-testid="why-us">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-16">
            <SectionEyebrow className="mb-4">Why NextChapter4You</SectionEyebrow>
            <h2 className="font-display text-3xl md:text-5xl text-[#1E2761] tracking-tight leading-tight">
              What Makes Us Different
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
            {WHY_US.map((w, i) => {
              const Icon = w.icon;
              return (
                <div
                  key={w.title}
                  data-testid={`why-us-${i}`}
                  className="bg-white p-8 md:p-10 hover:bg-[#F4F7FC] transition-colors"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <span className="font-display text-4xl text-[#C9A84C]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <Icon size={20} strokeWidth={1.5} className="text-[#1E2761]" />
                  </div>
                  <h3 className="font-display text-xl text-[#1E2761] mb-3 tracking-tight">
                    {w.title}
                  </h3>
                  <p className="text-sm text-[#334155]/80 leading-relaxed">
                    {w.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
